// Types
import { RootState } from "@/redux/store";

// Hooks
import { useSelector } from "react-redux";
import { useNotificationInfo, useNotificationSubscriptions } from "@/Common/hooks/Notifications";
import { useUserSubscriptions } from "@/Common/hooks/Users";

// Components
import { AnimatedDropdownMenu } from "./AnimatedDropdownMenu";
import ImageUserProfilePicture from "@/Layouts/components/ImageUserProfilePicture";
import DataCenterMenu, { DATA_CENTER_MENU_REQUIRED_ACTIONS } from "@/Layouts/views/Header/components/Menus/DataCenterMenu";
import EllipseMenu, { ELLIPSE_MENU_REQUIRED_ACTIONS } from "@/Layouts/views/Header/components/Menus/EllipseMenu";
import SettingsMenu, { SETTINGS_MENU_REQUIRED_ACTIONS } from "@/Layouts/views/Header/components/Menus/SettingsMenu";
import UserMenu from "@/Layouts/views/Header/components/Menus/UserMenu";
import { NotificationsMenu } from "@/Layouts/views/Header/components/Notifications";
import { HeaderActionIconButton } from "@/Layouts/views/Header/components/HeaderActionIconButton";

// Icons
import { ReactComponent as DataPagesIcon } from "@/assets/icons/header/dataPages/DataPagesIcon.svg";
import { ReactComponent as DotsIcon } from "@/assets/icons/header/ellipsis/dotsIcon.svg";
import { ReactComponent as SettingsHeaderIcon } from "@/assets/icons/header/settings/settingsIcon.svg";
import { ReactComponent as NotificationIcon } from "@/assets/icons/header/notifications/notificationIcon.svg";

// Constants | TODO: Un-mock tenant at some point
import { tenant } from "@/Constants/globalConfig";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

const UserIcon = () => (
	<ImageUserProfilePicture
		className="rounded-full border-white border-solid border-2"
		context="single"
		widthImg="24px"
		classNameHolder="h-5 bg-[transparent]"
		fillImgHolder="white"
	/>
);

export interface IHeaderActionsProps {
	onLogoutAction?: () => void;
}

export const HeaderActions = (props: Readonly<IHeaderActionsProps>) => {
	const user = useSelector((state: RootState) => state.user.user);
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);
	const { notificationInfo } = useNotificationInfo();
	const { useMultipleAuth } = useAuthorization();

	// Notification Subscriptions
	useNotificationSubscriptions({ userId: user?._id, locationId });

	// User Subscriptions
	useUserSubscriptions({ tenant });

	if (!user) {
		return null;
	}

	return (
		<AnimatedDropdownMenu
			caretPosition="end"
			caretClassNames="relative right-[17px]"
			ButtonSlot={HeaderActionIconButton}
			slotProps={[
				{
					id: "DataCenterMenu",
					iconButton: <DataPagesIcon className="text-white" />,
					disabled: !useMultipleAuth(DATA_CENTER_MENU_REQUIRED_ACTIONS, "atLeastOne"),
				},
				{
					id: "EllipseMenu",
					iconButton: <DotsIcon className="text-white" />,
					disabled: !useMultipleAuth(ELLIPSE_MENU_REQUIRED_ACTIONS, "atLeastOne"),
				},
				{
					id: "SettingsMenu",
					iconButton: <SettingsHeaderIcon className="text-white" />,
					disabled: !useMultipleAuth(SETTINGS_MENU_REQUIRED_ACTIONS, "atLeastOne"),
				},
				{
					id: "NotificationsMenu",
					iconButton: <NotificationIcon className="text-white" />,
					badgeNumber: notificationInfo?.newCount,
					title() {
						return notificationInfo?.totalCount ? "Open Notifications" : "There are no notifications";
					},
				},
				{ id: "UserMenu", iconButton: <UserIcon /> },
			]}
			menuDropdowns={[
				<DataCenterMenu key="DataCenterMenu" />,
				<EllipseMenu key="EllipseMenu" />,
				<SettingsMenu key="SettingsMenu" />,
				<NotificationsMenu key="NotificationsMenu" />,
				<UserMenu key="UserMenu" onLogoutAction={props.onLogoutAction} />,
			]}
		/>
	);
};
